<template>
  <Form class="search-filter-section grid-x grid-margin-x">
    <div class="cell hide-for-large margin-bottom-xxs search-bar-wrapper">
      <InputText
        v-model="searchValue"
        name="search-reviews"
        label="Search topic and reviews"
        text="Search"
        text-type="primary"
        @on-text-click="handleSearchButtonClick"
        @on-clear="handleSearchClearance"
        @keydown.enter="handleSearchButtonClick"
      />
    </div>
    <div id="reviewFilterBy" class="cell small-6 large-2">
      <Dropdown v-model="sortBy" label="Sort by" name="sort-by" :options="filterBySortOptions" :pt="REVIEWS_DROPDOWN_ARIA_DESCRIPTION" />
    </div>
    <div :class="`cell small-${searchQuery ? 12 : 6} large-2`">
      <Dropdown v-model="starFiltering" label="Filter by" name="filter-by" :options="filterByValidOptions" :pt="REVIEWS_DROPDOWN_ARIA_DESCRIPTION" />
    </div>
    <div class="cell show-for-large large-4 search-bar-wrapper">
      <InputText
        v-model="searchValue"
        name="search-reviews"
        label="Search topic and reviews"
        text="Search"
        text-type="primary"
        @on-text-click="handleSearchButtonClick"
        @on-clear="handleSearchClearance"
        @keydown.enter="handleSearchButtonClick"
      />
    </div>
  </Form>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import InputText from '@atomic-ui/inputText/InputText';
import Form from '@atomic-ui/form';
import Dropdown from '@atomic-ui/dropdown/Dropdown';

import useReviews from '~/modules/reviews/composables/useReviews';
import useReviewsToggle from '~/modules/reviews/composables/useReviewsToggle';
import useReviewsStatistics from '~/modules/reviews/composables/useReviewsStatistics';
import { useProduct } from '~/modules/product/composables/useProduct';
import { trackEvent } from '~/utils/bvPixel';
import useReviewsPhotos from '~/modules/reviews/composables/useReviewsPhotos';
import { FILTER_BY_OPTIONS, SORT_BY_OPTIONS, ALL_STAR_RATINGS_VALUE, SORT_BY_OPTION, REVIEWS_DROPDOWN_ARIA_DESCRIPTION } from '../constants/reviews';

const {
  sortBy: rawSortBy,
  starFiltering: rawStarFiltering,
  handleSortByChange,
  handleFilterByChange,
  handleSearchQueryChange,
  searchQuery,
} = useReviews();

const { expandReviews } = useReviewsToggle();

const { photos } = useReviewsPhotos();
const { countPerRating } = useReviewsStatistics();

const { productId } = useProduct();

const sortBy = computed({
  get() {
    return rawSortBy.value;
  },
  set(updatedSortBy) {
    expandReviews();
    handleSortByChange(updatedSortBy);
    trackEvent('sort', productId, updatedSortBy);
  },
});

const filterByValidOptions = computed(() => {
  const validStarOptions = countPerRating.value.reduce((accumulator, starRating) => {
    if (starRating.count > 0) accumulator.push(starRating.rating);
    return accumulator;
  }, []);
  validStarOptions.push(ALL_STAR_RATINGS_VALUE);

  return FILTER_BY_OPTIONS.map((option) => {
    if (!validStarOptions.includes(option.value)) {
      return { ...option, disabled: true };
    }
    return option;
  });
});

const starFiltering = computed({
  get() {
    return String(rawStarFiltering.value);
  },
  set(updatedStarFilteringValue) {
    expandReviews();
    handleFilterByChange(updatedStarFilteringValue);
    trackEvent('filter', productId, 'stars', updatedStarFilteringValue);
  },
});

const filterBySortOptions = computed(() =>
  SORT_BY_OPTIONS.map((option) => {
    if (option.value === SORT_BY_OPTION.PHOTO_REVIEWS) {
      return { ...option, disabled: !photos?.value?.length };
    }
    return option;
  }),
);

const searchValue = ref('');
const handleSearchButtonClick = () => {
  handleSearchQueryChange(searchValue.value);
  trackEvent('search', productId, searchValue.value);
};
const handleSearchClearance = () => handleSearchQueryChange('');

// fires when the value is reset from outside, e.g. by clicking "frequently mentioned"
watch(searchQuery, () => {
  if (searchQuery.value !== searchValue.value) {
    searchValue.value = searchQuery.value;
  }
});
</script>

<style scoped lang="scss">
.search-filter-section {
  div.search-bar-wrapper {
    margin-left: auto;
  }
}
</style>
